/*
 * Contact Table
 *
 * Features:
 * - Renders schema-driven team and RLP contact grids with filtering and export data
 * - Reuses the shared team-contact server cache across remounts
 * - Uses a compact mobile contact list while preserving the schema grid on tablet and desktop
 *
 * Changes:
 * - 2026-09-04: Opens desktop and mobile RLP rows in the permission-aware Contact Overview
 * - 2026-08-18: Remounts the data grid when the selected schema changes so row navigation uses the current dropdown
 * - 2026-08-06: Replaced component-owned contact fetching with TanStack Query
 */

import { FC, useMemo, useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { Contact } from '../../models/Contact';
import { type FormField } from '../../components/common/FormField';
import Grid from '../common/Grid';
import { type Schema } from '../../context/SchemaContext';
import { schemaFieldsToColumnDefs } from '../../utils/columnUtils';
import { createNameCellRenderer } from '../../utils/gridCellRenderers';
import { ColDef, SortDirection, ValueGetterParams } from 'ag-grid-community';
import Spinner from '../common/Spinner';
import { useTeamContactsQuery } from '../../query/contactQueries';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { Button } from '../primitives';

const RLP_TEAM_ID = -1; // Special teamId value for RLP legacy records
const MOBILE_CONTACTS_QUERY = '(max-width: 767px)';
const MOBILE_SUMMARY_FIELDS = new Set([
  'accountName',
  'companyName',
  'title',
  'jobTitle',
  'email',
  'emailAddress',
  'phone',
  'cellPhone',
  'preferredLanguage',
]);

const isMobileContactsViewport = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(MOBILE_CONTACTS_QUERY).matches
);

interface ContactTableProps {
  searchQuery: string;
  schema: Schema | null;
  onRowCountChange: (count: number) => void;
  onDataChange?: (data: Contact[], columns: Array<{field: string, headerName: string}>) => void;
  onClearSearch: () => void;
  onCreateContact: () => void;
}

const ContactTable: FC<ContactTableProps> = ({
  searchQuery,
  schema,
  onRowCountChange,
  onDataChange,
  onClearSearch,
  onCreateContact,
}) => {
  const navigate = useNavigate();
  const [agGridFilteredData, setAgGridFilteredData] = useState<Contact[]>([]);
  const [isMobile, setIsMobile] = useState(isMobileContactsViewport);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_CONTACTS_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    setIsMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Callback handler for AG-Grid filtered data changes
  const handleFilteredDataChange = useCallback((filteredData: Contact[]) => {
    setAgGridFilteredData(filteredData);
  }, []);

  const { schemaFields, schemaError } = useMemo(() => {
    if (!schema) return { schemaFields: [] as FormField[], schemaError: null as string | null };
    try {
      const fields: FormField[] = JSON.parse(schema.schema);
      return { schemaFields: fields, schemaError: null as string | null };
    } catch (error) {
      console.error('Error parsing schema:', error);
      return {
        schemaFields: [] as FormField[],
        schemaError: 'Invalid schema configuration. Please contact support.',
      };
    }
  }, [schema]);

  const teamId = schema && !schemaError ? (schema.rlp ? RLP_TEAM_ID : schema.teamId) : null;
  const contactsQuery = useTeamContactsQuery(teamId);
  const contacts = contactsQuery.data ?? [];
  const loading = Boolean(schema && !schemaError && contactsQuery.isPending);
  const error = schemaError
    ?? (contactsQuery.error instanceof Error ? contactsQuery.error.message : contactsQuery.error ? 'Failed to load contacts' : null);

  // Memoize name cell renderer separately to avoid column regeneration on search
  const nameRenderer = useMemo(() =>
    createNameCellRenderer(searchQuery),
    [searchQuery]
  );

  // Generate column definitions dynamically from schema fields
  const columnDefs = useMemo(() => {
    // Base columns that are always present
    const baseColumns: ColDef<Contact>[] = [
      {
        headerName: '',
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'left' as const,
        cellStyle: { borderRight: '1px solid #ddd' },
        valueGetter: (params: ValueGetterParams<Contact>) => {
          return (params.node?.rowIndex ?? 0) + 1;
        },
        colId: 'rowNumber',
        sortable: false,
        filter: false
      },
      {
        field: 'displayName',
        headerName: 'Name',
        sortable: true,
        width: 300,
        pinned: true,
        cellRenderer: nameRenderer
      },
      {
        field: 'noteCount',
        headerName: 'Note Count',
        sortable: true,
        sort: 'desc' as SortDirection
      }
    ];

    // Filter out displayName and noteCount from schema fields to avoid duplicates
    const filteredSchemaFields = schemaFields.filter(
      field => field.name !== 'displayName' && field.name !== 'noteCount'
    );

    // Add schema-based columns
    const schemaColumns = schemaFieldsToColumnDefs(filteredSchemaFields);

    return [...baseColumns, ...schemaColumns];
  }, [schemaFields, nameRenderer]);

  // Filter contacts based on search (text filtering only, team filtering done server-side)
  const filteredContacts = useMemo(() => {
    if (!searchQuery) return contacts;

    const query = searchQuery.toLowerCase();
    return contacts.filter(contact => {
      // Check all visible fields for search match
      return schemaFields.some((field: FormField) => {
        if (!(field.name in contact)) return false;
        const value = contact[field.name as keyof Contact];
        return value && String(value).toLowerCase().includes(query);
      }) || (contact.displayName && String(contact.displayName).toLowerCase().includes(query));
    });
  }, [contacts, searchQuery, schemaFields]);

  // Enhanced data and export management - prioritize AG-Grid filtered data over search filtering
  useEffect(() => {
    // Only run when we have actual data and are not loading
    if (loading || columnDefs.length === 0) return;

    // Determine which data to use for export:
    // 1. AG-Grid filtered data (if available) - includes column filters + search filters
    // 2. Fallback to search-only filtered data
    const exportData = !isMobile && agGridFilteredData.length > 0 ? agGridFilteredData : filteredContacts;

    // Always update row count with current filtered data
    onRowCountChange(exportData.length);

    // Provide export data only if callback exists
    if (onDataChange) {
      const exportColumns = columnDefs
        .filter(col => col.field && col.field !== 'rowNumber')
        .map(col => ({
          field: col.field!,
          headerName: col.headerName || col.field!
        }));
      onDataChange(exportData, exportColumns);
    }
  }, [filteredContacts, agGridFilteredData, isMobile, loading, onRowCountChange, onDataChange, columnDefs]);

  const gridOptions = useMemo(() => ({
    onRowClicked: (params: { data: Contact }) => {
      navigate(`/contacts/${params.data.id}`);
    }
  }), [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center h-64 flex items-center justify-center">
        {error}
      </div>
    );
  }

  if (filteredContacts.length === 0) {
    return (
      <div className={`flex h-64 flex-col items-center justify-center gap-3 px-6 text-center ${THEME_CLASSES.text.secondary}`}>
        <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>
          {searchQuery ? 'No contacts match your search' : 'No contacts in this contact type'}
        </h2>
        <p className="max-w-md text-sm">
          {searchQuery
            ? 'Try another term or clear the current search.'
            : 'Create a contact to start this contact list.'}
        </p>
        <Button variant="secondary" size="sm" onClick={searchQuery ? onClearSearch : onCreateContact}>
          {searchQuery ? 'Clear search' : 'New contact'}
        </Button>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2" role="list" aria-label="Contacts">
        {filteredContacts.map((contact) => {
          const summaryFields = schemaFields
            .filter((field) => MOBILE_SUMMARY_FIELDS.has(field.name))
            .map((field) => ({ label: field.label || field.name, value: contact[field.name as keyof Contact] }))
            .filter(({ value }) => value !== null && value !== undefined && value !== '')
            .slice(0, 1);
          const noteCount = contact.noteCount ?? 0;
          const summaryText = [
            `${noteCount} ${noteCount === 1 ? 'note' : 'notes'}`,
            ...summaryFields.map(({ label, value }) => `${label}: ${String(value)}`),
          ].join(' · ');

          return (
            <div key={contact.id} role="listitem" className="mb-2">
              <Link
                to={`/contacts/${contact.id}`}
                className={`flex min-h-16 w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left ${THEME_CLASSES.card.background} ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.hover.full}`}
              >
                <span className="min-w-0">
                  <span className="sr-only">Open contact: </span>
                  <span className={`block truncate text-sm font-semibold ${THEME_CLASSES.text.primary}`}>{contact.displayName}</span>
                  <span className={`mt-1 block truncate text-xs ${THEME_CLASSES.text.secondary}`}>
                    {summaryText}
                  </span>
                </span>
                <svg className={`h-4 w-4 shrink-0 ${THEME_CLASSES.text.secondary}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className='h-[calc(100vh-150px)]'>
      <Grid
        key={`contacts-${schema?.id || 'default'}`}
        name={`contacts-${schema?.id || 'default'}`}
        data={filteredContacts}
        columnDefs={columnDefs}
        searchQuery={searchQuery}
        gridOptions={gridOptions}
        columnSelectorPortalTargetId="grid-column-selector-portal"
        onFilteredDataChange={handleFilteredDataChange}
      />
    </div>
  );
};

export default ContactTable;
